// Table Grid Component - Header Row Management via Buttons
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Trash2, Plus, Link2, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';
import type { 
  TableConfig, 
  TableSelection, 
  TableCell as TableCellType,
  GroupConfig,
  TotalsRowConfig,
  AggregationType
} from '@/app/types/table';
import type { PlacedBlock, SocketConnection } from '@/app/types/cad';
import { getCellRenderInfo } from '@/app/utils/tableUtils';
import { calculateAllAggregations } from '@/app/utils/aggregationUtils';
import { TotalsRow } from './TotalsRow';

interface TableGridProps {
  tableConfig: TableConfig;
  selection: TableSelection | null;
  editingCell: { rowId: string; colId: string } | null;
  onSelectionChange: (selection: TableSelection | null) => void;
  onCellEdit: (cell: { rowId: string; colId: string } | null) => void;
  onCellValueChange: (rowId: string, colId: string, value: string | number | boolean) => void;
  onDeleteRow: (rowIndex: number) => void;
  onAddColumn?: () => void;
  onDeleteColumn?: (columnId: string) => void;
  onAttributeDrop?: (attributeName: string, columnIndex?: number) => void;
  onColumnResize?: (columnId: string, newWidth: number) => void;
  onColumnReorder?: (fromIndex: number, toIndex: number) => void;
  onHeaderDoubleClick?: (columnIndex: number) => void;
  onMergedColumnDoubleClick?: (columnIndex: number) => void;
  onHeaderSelectionChange?: (selection: number[] | null) => void;
  onHeaderGroupClick?: (groupId: string) => void;
  onAddHeaderRow?: () => void;
  onInsertHeaderRowAbove?: (headerRowId: string) => void;
  onDeleteHeaderRow?: (headerRowId: string) => void;
  onResetHeaderRows?: () => void;
  // Header cell selection
  headerCellSelection?: Array<{ rowId: string; cellId: string }>;
  onHeaderCellClick?: (rowId: string, cellId: string, ctrlKey: boolean) => void;
  // Header cell editing
  editingHeaderCell?: { rowId: string; cellId: string } | null;
  onHeaderCellEdit?: (cell: { rowId: string; cellId: string } | null) => void;
  onHeaderCellValueChange?: (rowId: string, cellId: string, newValue: string) => void;
  // Grouping
  onToggleGroupCollapse?: (groupKey: string) => void;
  // Totals Row
  onUpdateTotalsAggregation?: (columnId: string, aggregation: AggregationType) => void;
}

export function TableGrid({
  tableConfig,
  selection,
  editingCell,
  onSelectionChange,
  onCellEdit,
  onCellValueChange,
  onDeleteRow,
  onAddColumn,
  onDeleteColumn,
  onAttributeDrop,
  onColumnResize,
  onColumnReorder,
  onHeaderDoubleClick,
  onMergedColumnDoubleClick,
  onHeaderSelectionChange,
  onHeaderGroupClick,
  onAddHeaderRow,
  onInsertHeaderRowAbove,
  onDeleteHeaderRow,
  onResetHeaderRows,
  headerCellSelection = [],
  onHeaderCellClick,
  editingHeaderCell,
  onHeaderCellEdit,
  onHeaderCellValueChange,
  onToggleGroupCollapse,
  onUpdateTotalsAggregation,
}: TableGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ row: number; col: number } | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [dropTargetColumn, setDropTargetColumn] = useState<number | null>(null);
  const [hoveredResizeColumn, setHoveredResizeColumn] = useState<string | null>(null);
  
  // Column reordering state
  const [draggedColumnIndex, setDraggedColumnIndex] = useState<number | null>(null);
  const [dropIndicatorIndex, setDropIndicatorIndex] = useState<number | null>(null);
  
  // Preview line for resize (Excel-style!)
  const [resizePreviewX, setResizePreviewX] = useState<number | null>(null);
  const resizePreviewXRef = useRef<number | null>(null); // Ref for synchronous access!
  
  // Use REF for resize state to avoid closure issues
  const resizeStateRef = useRef<{
    columnId: string;
    startX: number;
    startWidth: number;
    thElement: HTMLTableCellElement;
    columnLeftEdge: number;
    offsetFromCursorToRightEdge: number;
  } | null>(null);

  // ===== TOTALS ROW CALCULATION =====
  const totalsRowData = useMemo(() => {
    if (!tableConfig.totalsRowConfig?.enabled) {
      return {};
    }
    
    // Filter out group/subtotal/total rows
    const dataRows = tableConfig.rows.filter(
      row => !row.isHeader && !row.isGroupHeader && !row.isSubtotal && !row.isGrandTotal
    );
    
    return calculateAllAggregations(
      dataRows,
      tableConfig.totalsRowConfig.aggregations,
      tableConfig.columns
    );
  }, [tableConfig.rows, tableConfig.totalsRowConfig, tableConfig.columns]);

  // Refs for event handlers to avoid stale closures
  const handleResizeMoveRef = useRef<((e: MouseEvent) => void) | null>(null);
  const handleResizeEndRef = useRef<(() => void) | null>(null);

  const handleResizeStart = useCallback((e: React.MouseEvent, columnId: string, currentWidth: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clean up any existing listeners first!
    if (handleResizeMoveRef.current) {
      window.removeEventListener('mousemove', handleResizeMoveRef.current);
    }
    if (handleResizeEndRef.current) {
      window.removeEventListener('mouseup', handleResizeEndRef.current);
    }
    
    const th = (e.currentTarget as HTMLElement).closest('th') as HTMLTableCellElement;
    if (!th) return;
    
    // Get the REAL current width and position from the DOM
    const rect = th.getBoundingClientRect();
    const realCurrentWidth = rect.width;
    const columnRightEdge = rect.right;
    const cursorX = e.clientX;
    const offsetFromCursorToRightEdge = columnRightEdge - cursorX;
    
    console.log('🟢 RESIZE START:', { 
      columnId, 
      '1_cursorX_beim_Klick': cursorX,
      '2_columnRightEdge_beim_Klick': columnRightEdge,
      '3_offsetFromCursorToRightEdge': offsetFromCursorToRightEdge,
      '4_realCurrentWidth': realCurrentWidth,
    });
    
    resizeStateRef.current = {
      columnId,
      startX: cursorX,
      startWidth: realCurrentWidth,
      thElement: th,
      columnLeftEdge: rect.left,
      offsetFromCursorToRightEdge,
    };
    
    setHoveredResizeColumn(columnId);
    
    // Initialisiere die Preview-Line sofort mit der aktuellen Position!
    const containerRect = containerRef.current?.getBoundingClientRect();
    const containerLeft = containerRect?.left || 0;
    const initialPreviewX = columnRightEdge - containerLeft;
    setResizePreviewX(initialPreviewX);
    resizePreviewXRef.current = initialPreviewX;

    // Create NEW handlers
    const handleMove = (ev: MouseEvent) => {
      const state = resizeStateRef.current;
      if (!state) return;

      const currentCursorX = ev.clientX;
      
      // Die rechte Kante soll den Offset zum Cursor beibehalten
      const newRightEdge = currentCursorX + state.offsetFromCursorToRightEdge;
      
      // WICHTIG: Konvertiere viewport-Koordinate zu Container-relative Koordinate!
      const containerRect = containerRef.current?.getBoundingClientRect();
      const containerLeft = containerRect?.left || 0;
      const relativeX = newRightEdge - containerLeft;
      
      // WICHTIG: Während des Resizings NUR die Preview-Line zeigen!
      // Die tatsächliche Spaltenbreite wird erst beim MouseUp gesetzt!
      setResizePreviewX(relativeX);
      resizePreviewXRef.current = relativeX;
    };

    const handleEnd = () => {
      const state = resizeStateRef.current;
      if (!state) return;

      // Berechne die finale Breite basierend auf der Preview-Line Position
      if (resizePreviewXRef.current !== null) {
        const containerRect = containerRef.current?.getBoundingClientRect();
        const containerLeft = containerRect?.left || 0;
        const containerScrollLeft = containerRef.current?.scrollLeft || 0;
        const absoluteRightEdge = resizePreviewXRef.current + containerLeft + containerScrollLeft;
        const finalWidth = Math.max(1, absoluteRightEdge - state.columnLeftEdge);
        
        console.log('✅ RESIZE END:', {
          columnId: state.columnId,
          startWidth: state.startWidth,
          resizePreviewX: resizePreviewXRef.current,
          containerLeft: containerLeft,
          containerScrollLeft: containerScrollLeft,
          absoluteRightEdge: absoluteRightEdge,
          columnLeftEdge: state.columnLeftEdge,
          finalWidth: finalWidth,
          calculation: `${absoluteRightEdge} - ${state.columnLeftEdge} = ${finalWidth}`,
        });
        
        if (onColumnResize) {
          onColumnResize(state.columnId, finalWidth);
        }
      } else {
        console.log('❌ RESIZE END: resizePreviewX is null!');
      }

      // Clean up
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      handleResizeMoveRef.current = null;
      handleResizeEndRef.current = null;

      resizeStateRef.current = null;
      setHoveredResizeColumn(null);
      setResizePreviewX(null);
      resizePreviewXRef.current = null;
    };

    // Store refs and register
    handleResizeMoveRef.current = handleMove;
    handleResizeEndRef.current = handleEnd;
    
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
  }, [onColumnResize]);

  // Auto-fit column width on double click
  const handleAutoFit = useCallback((e: React.MouseEvent, columnIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (!tableRef.current || !onColumnResize) return;

    const column = tableConfig.columns[columnIndex];
    if (!column) return;

    // Create a temporary canvas to measure text width accurately
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    let maxWidth = 30; // Minimum width (very small)

    // Measure header text with bold font
    context.font = 'bold 12px system-ui, -apple-system, sans-serif';
    const headerWidth = context.measureText(column.label).width + 4; // 2px left + 2px right
    maxWidth = Math.max(maxWidth, headerWidth);

    // Measure all cell texts in this column
    context.font = '12px system-ui, -apple-system, sans-serif';
    tableConfig.rows.forEach(row => {
      const cell = row.data[column.id];
      if (cell && cell.value !== null && cell.value !== undefined) {
        const text = String(cell.value);
        const textWidth = context.measureText(text).width + 4; // 2px left + 2px right
        maxWidth = Math.max(maxWidth, textWidth);
      }
    });

    // Add cell padding (8px left + 8px right = 16px total)
    maxWidth = maxWidth + 16;

    // Cap maximum width to prevent extremely wide columns
    maxWidth = Math.min(maxWidth, 600);

    onColumnResize(column.id, Math.ceil(maxWidth));
  }, [tableConfig, onColumnResize]);

  // ===== NEW: Calculate headerRowSpan for each column =====
  // Columns NOT in any HeaderGroup should span ALL header rows
  const calculateHeaderRowSpan = useCallback((colIdx: number): number => {
    // Check if tableConfig.headerGroups exists and has items
    if (!tableConfig.headerGroups || tableConfig.headerGroups.length === 0) {
      return 1; // No header groups, so main header is the only row
    }

    const column = tableConfig.columns[colIdx];
    if (!column) return 1;

    // NEW: Check if this column ID is in ANY header group
    const isInAnyGroup = tableConfig.headerGroups.some(
      group => group.columnIds.includes(column.id)
    );

    if (isInAnyGroup) {
      return 1; // Column is in a group, so it only spans the main header row
    } else {
      // Column is NOT in any group, so it should span ALL header rows
      const numGroupLevels = Math.max(...tableConfig.headerGroups.map(g => g.level)) + 1;
      return numGroupLevels + 1; // +1 for the main header row
    }
  }, [tableConfig.headerGroups, tableConfig.columns]);

  // Handle cell click for selection
  const handleCellMouseDown = useCallback((rowIndex: number, colIndex: number, event: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ row: rowIndex, col: colIndex });
    onSelectionChange({
      startRow: rowIndex,
      startCol: colIndex,
      endRow: rowIndex,
      endCol: colIndex,
    });
  }, [onSelectionChange]);

  // Handle cell drag for multi-selection
  const handleCellMouseEnter = useCallback((rowIndex: number, colIndex: number) => {
    if (!isDragging || !dragStart) return;

    onSelectionChange({
      startRow: Math.min(dragStart.row, rowIndex),
      startCol: Math.min(dragStart.col, colIndex),
      endRow: Math.max(dragStart.row, rowIndex),
      endCol: Math.max(dragStart.col, colIndex),
    });
  }, [isDragging, dragStart, onSelectionChange]);

  // Handle mouse up
  useEffect(() => {
    const handleMouseUp = () => {
      setIsDragging(false);
      setDragStart(null);
    };

    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // Handle double click for editing
  const handleCellDoubleClick = useCallback((rowId: string, colId: string) => {
    const row = tableConfig.rows.find(r => r.id === rowId);
    const cell = row?.data[colId];
    
    if (cell && !cell.isReadOnly) {
      onCellEdit({ rowId, colId });
    }
  }, [tableConfig, onCellEdit]);

  // Handle drag over for attribute drop
  const handleDragOver = useCallback((e: React.DragEvent, columnIndex?: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropTargetColumn(columnIndex !== undefined ? columnIndex : null);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropTargetColumn(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, columnIndex?: number) => {
    e.preventDefault();
    const attributeName = e.dataTransfer.getData('text/plain');
    if (attributeName && onAttributeDrop) {
      onAttributeDrop(attributeName, columnIndex);
    }
    setDropTargetColumn(null);
  }, [onAttributeDrop]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selection || editingCell) return;

      let newRow = selection.startRow;
      let newCol = selection.startCol;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          newRow = Math.max(0, selection.startRow - 1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          newRow = Math.min(tableConfig.rows.length - 1, selection.startRow + 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          newCol = Math.max(0, selection.startCol - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          newCol = Math.min(tableConfig.columns.length - 1, selection.startCol + 1);
          break;
        case 'Enter':
          e.preventDefault();
          const row = tableConfig.rows[selection.startRow];
          const colId = tableConfig.columns[selection.startCol]?.id;
          if (row && colId) {
            handleCellDoubleClick(row.id, colId);
          }
          break;
        case 'Tab':
          e.preventDefault();
          newCol = e.shiftKey 
            ? Math.max(0, selection.startCol - 1)
            : Math.min(tableConfig.columns.length - 1, selection.startCol + 1);
          break;
        case 'Delete':
          e.preventDefault();
          if (selection) {
            for (let r = selection.startRow; r <= selection.endRow; r++) {
              for (let c = selection.startCol; c <= selection.endCol; c++) {
                const row = tableConfig.rows[r];
                const colId = tableConfig.columns[c]?.id;
                if (row && colId && !row.data[colId]?.isReadOnly) {
                  onCellValueChange(row.id, colId, '');
                }
              }
            }
          }
          break;
        default:
          return;
      }

      if (newRow !== selection.startRow || newCol !== selection.startCol) {
        onSelectionChange({
          startRow: newRow,
          startCol: newCol,
          endRow: newRow,
          endCol: newCol,
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selection, editingCell, tableConfig, onSelectionChange, onCellValueChange, handleCellDoubleClick]);

  return (
    <div
      ref={containerRef}
      className="relative h-full"
      style={{
        backgroundColor: 'var(--md-surface)',
        cursor: resizeStateRef.current ? 'ew-resize' : 'default', // Show resize cursor while dragging
      }}
    >
      {tableConfig.columns.length === 0 ? (
        // Empty state - show drop zone
        <div
          className="flex items-center justify-center absolute inset-4"
          onDragOver={(e) => handleDragOver(e)}
          onDrop={(e) => handleDrop(e)}
          onDragLeave={handleDragLeave}
          style={{
            border: dropTargetColumn === null ? '2px dashed var(--md-outline)' : '2px solid var(--md-primary)',
            backgroundColor: dropTargetColumn === null ? 'transparent' : 'rgba(var(--md-primary-rgb, 103, 80, 164), 0.05)',
            borderRadius: '8px',
          }}
        >
          <div className="text-center px-8 py-12">
            <div
              className="text-lg font-medium mb-2"
              style={{ color: 'var(--md-on-surface)' }}
            >
              {dropTargetColumn === null ? 'Ziehe Attribute hierher' : 'Attribut hier ablegen'}
            </div>
            <div
              className="text-sm"
              style={{ color: 'var(--md-on-surface-variant)' }}
            >
              Wähle Attribute aus dem linken Panel und ziehe sie in die Tabelle
            </div>
          </div>
        </div>
      ) : (
        <div className="flex">
          <table
            ref={tableRef}
            className="border-collapse"
            style={{
              borderSpacing: 0,
              tableLayout: 'fixed',
              // WICHTIG: Exakte Summe der Spaltenbreiten - KEIN 100%!
              width: tableConfig.columns.reduce((sum, col) => sum + col.width, 0),
              // LEVEL 1: Außenrahmen (3px solid black)
              border: '3px solid #000000',
            }}
          >
            {/* COLGROUP - Der EINZIGE Weg um Spaltenbreiten mit table-layout:fixed zu setzen! */}
            <colgroup>
              {tableConfig.columns.map((col) => (
                <col key={col.id} style={{ width: `${col.width}px` }} />
              ))}
            </colgroup>
            
            <thead>
              {/* Add Header Row Button - appears at the top */}
              {onAddHeaderRow && (
                <tr style={{ height: 24 }}>
                  <th
                    colSpan={tableConfig.columns.length}
                    onClick={onAddHeaderRow}
                    className="transition-colors cursor-pointer"
                    style={{
                      borderTop: 'none', // Top border is handled by table border
                      borderRight: 'none',
                      borderBottom: '1px dashed #ccc',
                      borderLeft: 'none',
                      backgroundColor: '#ffffff',
                      textAlign: 'center',
                      padding: '4px 8px',
                      fontSize: '12px',
                      color: 'var(--md-primary)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(var(--md-primary-rgb, 103, 80, 164), 0.05)';
                      e.currentTarget.style.borderBottom = '1px solid var(--md-primary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff'; // 🔥 Keep solid background, not transparent
                      e.currentTarget.style.borderBottom = '1px dashed #ccc';
                    }}
                    title="Header-Zeile am Anfang hinzufügen"
                  >
                    <Plus className="w-3 h-3 inline mr-1" />
                    Header-Zeile hinzufügen
                  </th>
                </tr>
              )}
              
              {/* headerRows */}
              {tableConfig.headerRows && tableConfig.headerRows.length > 0 && (
                <>
                  {tableConfig.headerRows.map((headerRow, rowIdx, allHeaderRows) => {
                    const isLastHeaderRow = rowIdx === allHeaderRows.length - 1;
                    // NEW: Build mapping of cell index to column start index FIRST
                    const colStartMapping: number[] = [];
                    let mappingCol = 0;
                    headerRow.cells.forEach((c) => {
                      colStartMapping.push(mappingCol);
                      mappingCol += c.colspan || 1;
                    });
                    
                    return (
                      <tr key={headerRow.id} style={{ height: 40 }}>
                        {headerRow.cells
                          .map((cell, cellIdx) => {
                            // Skip cells with colspan=0 (they are part of a previous merged cell)
                            if (cell.colspan === 0) return null;
                            
                            // NEW: Check if any column covering this cell has skipHeaderRows
                            const cellColStart = colStartMapping[cellIdx];
                            const cellColEnd = cellColStart + (cell.colspan || 1) - 1;
                            
                            let shouldSkipCell = false;
                            for (let colIdx = cellColStart; colIdx <= cellColEnd; colIdx++) {
                              const column = tableConfig.columns[colIdx];
                              if (column && column.skipHeaderRows && column.skipHeaderRows > rowIdx) {
                                // This column skips this header row - don't render this cell
                                shouldSkipCell = true;
                                console.log(`⏭️ Skipping headerRow[${rowIdx}].cell[${cellIdx}] col ${colIdx} - covered by column "${column.label}" with skipHeaderRows=${column.skipHeaderRows}`);
                                break;
                              }
                            }
                            
                            if (shouldSkipCell) {
                              return null;
                            }
                        
                        // Check if this cell is selected
                        const isSelected = headerCellSelection.some(
                          sel => sel.rowId === headerRow.id && sel.cellId === cell.id
                        );
                        
                        // Extract borders from cell style
                        const cellBorders = cell.style?.borders || {};
                        const isLastCell = cellIdx === headerRow.cells.filter(c => c.colspan !== 0).length - 1;
                        const defaultBorderTop = '1px solid #d0d0d0';
                        const defaultBorderRight = isLastCell ? 'none' : '1px solid #d0d0d0';
                        const defaultBorderBottom = '1px solid #d0d0d0'; // FIXED: No thick border here! Separator row handles it
                        const defaultBorderLeft = cellIdx === 0 ? 'none' : '1px solid #d0d0d0';
                        
                        return (
                          <th
                            key={cell.id}
                            colSpan={cell.colspan}
                            rowSpan={cell.rowspan || 1}
                            onClick={(e) => {
                              if (editingHeaderCell) return;
                              
                              e.stopPropagation();
                              
                              // Single click always handles selection
                              if (onHeaderCellClick) {
                                onHeaderCellClick(headerRow.id, cell.id, e.ctrlKey || e.metaKey);
                              }
                            }}
                            onDoubleClick={(e) => {
                              // Double click starts editing (for both empty and filled cells)
                              if (onHeaderCellEdit) {
                                e.stopPropagation();
                                onHeaderCellEdit({ rowId: headerRow.id, cellId: cell.id });
                              }
                            }}
                            style={{
                              // Apply custom borders if defined, otherwise use defaults
                              borderTop: cellBorders.top 
                                ? (cellBorders.top.visible ? `${cellBorders.top.width}px ${cellBorders.top.style} ${cellBorders.top.color}` : 'none')
                                : defaultBorderTop,
                              borderRight: cellBorders.right
                                ? (cellBorders.right.visible ? `${cellBorders.right.width}px ${cellBorders.right.style} ${cellBorders.right.color}` : 'none')
                                : defaultBorderRight,
                              borderBottom: cellBorders.bottom
                                ? (cellBorders.bottom.visible ? `${cellBorders.bottom.width}px ${cellBorders.bottom.style} ${cellBorders.bottom.color}` : 'none')
                                : defaultBorderBottom,
                              borderLeft: cellBorders.left
                                ? (cellBorders.left.visible ? `${cellBorders.left.width}px ${cellBorders.left.style} ${cellBorders.left.color}` : 'none')
                                : defaultBorderLeft,
                              backgroundColor: cell.style?.backgroundColor || (isSelected 
                                ? 'rgba(var(--md-primary-rgb, 103, 80, 164), 0.25)' 
                                : cell.label ? '#e8e8e8' : '#f8f8f8'),
                              fontWeight: cell.label ? 'bold' : 'normal',
                              textAlign: 'center',
                              padding: '8px',
                              overflow: 'visible', // Allow delete button to overflow
                              cursor: onHeaderCellClick ? 'pointer' : 'default',
                              transition: 'background-color 0.15s',
                            }}
                          >
                            {/* Delete-Button - Only show for cells with label */}
                            {cell.label && onDeleteHeaderRow && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (confirm(`Überschrift "${cell.label}" wirklich löschen?`)) {
                                    onDeleteHeaderRow(headerRow.id);
                                  }
                                }}
                                className="absolute transition-opacity"
                                style={{
                                  top: '4px',
                                  left: '4px',
                                  color: '#d32f2f',
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  borderRadius: '4px',
                                  padding: '2px',
                                  cursor: 'pointer',
                                  border: '1px solid rgba(211, 47, 47, 0.3)',
                                  zIndex: 40,
                                  opacity: 0.3,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.opacity = '1';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.opacity = '0.3';
                                }}
                                title="Header-Zeile löschen"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                            
                            {/* Insert Above Button - Only show for cells with label */}
                            {cell.label && onInsertHeaderRowAbove && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onInsertHeaderRowAbove(headerRow.id);
                                }}
                                className="absolute transition-opacity"
                                style={{
                                  top: '4px',
                                  right: '4px',
                                  color: 'var(--md-primary)',
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  borderRadius: '4px',
                                  padding: '2px',
                                  cursor: 'pointer',
                                  border: '1px solid rgba(var(--md-primary-rgb, 103, 80, 164), 0.3)',
                                  zIndex: 40,
                                  opacity: 0.3,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.opacity = '1';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.opacity = '0.3';
                                }}
                                title="Header-Zeile darüber einfügen"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            )}
                            
                            {/* Editable cell content */}
                            {editingHeaderCell && 
                             editingHeaderCell.rowId === headerRow.id && 
                             editingHeaderCell.cellId === cell.id ? (
                              <input
                                type="text"
                                defaultValue={cell.label || ''}
                                autoFocus
                                onBlur={(e) => {
                                  if (onHeaderCellValueChange) {
                                    onHeaderCellValueChange(headerRow.id, cell.id, e.target.value);
                                  }
                                  if (onHeaderCellEdit) {
                                    onHeaderCellEdit(null);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.currentTarget.blur();
                                  } else if (e.key === 'Escape') {
                                    if (onHeaderCellEdit) {
                                      onHeaderCellEdit(null);
                                    }
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  width: '100%',
                                  border: '2px solid var(--md-primary)',
                                  borderRadius: '2px',
                                  padding: '4px',
                                  textAlign: 'center',
                                  fontWeight: 'bold',
                                  backgroundColor: 'white',
                                  outline: 'none',
                                }}
                              />
                            ) : (
                              cell.label
                            )}
                          </th>
                        );
                      })
                      .filter((element): element is React.ReactElement => element !== null)
                    }
                    </tr>
                    );
                  })}
                </>
              )}
              
              {/* Header groups removed - no longer supported */}
              {false && tableConfig.headerGroups && tableConfig.headerGroups.length > 0 && (
                <>
                  {/* Group rows by level */}
                  {Array.from(new Set(tableConfig.headerGroups.map(g => g.level)))
                    .sort((a, b) => b - a) // Highest level first (top row)
                    .map((level, levelIdx) => {
                      const groupsAtLevel = tableConfig.headerGroups!.filter(g => g.level === level);
                      const numHeaderRows = tableConfig.headerRows?.length || 0;
                      const addButtonHeight = onAddHeaderRow ? 24 : 0;
                      const topOffset = addButtonHeight + (numHeaderRows * 40) + (levelIdx * 40); // Stack below add button and headerRows
                      
                      return (
                        <tr key={`group-level-${level}`} style={{ height: 40 }}>
                          {tableConfig.columns
                            .map((col, colIdx) => {
                              // Skip columns that span multiple rows (they were already rendered in main header row)
                              const rowSpan = calculateHeaderRowSpan(colIdx);
                              if (rowSpan > 1) {
                                // This column is already rendered in the main header row with rowSpan
                                return null;
                              }
                              
                              // NEW: Check if this column ID is part of a group at this level
                              const group = groupsAtLevel.find(g => g.columnIds.includes(col.id));
                              
                              if (group) {
                                // Find the first column in this group
                                const firstColInGroup = tableConfig.columns.find(c => c.id === group.columnIds[0]);
                                const firstColIdx = tableConfig.columns.findIndex(c => c.id === group.columnIds[0]);
                                
                                // Only render if this is the first column in the group
                                if (col.id === firstColInGroup?.id) {
                                  const colspan = group.columnIds.length;
                                  const totalWidth = group.columnIds
                                    .map(id => tableConfig.columns.find(c => c.id === id))
                                    .filter(Boolean)
                                    .reduce((sum, c) => sum + (c?.width || 0), 0);
                                  
                                  return (
                                    <th
                                      key={`${group.id}-${colIdx}`}
                                      colSpan={colspan}
                                      style={{
                                        width: totalWidth,
                                        minWidth: totalWidth,
                                        maxWidth: totalWidth,
                                        borderTop: '2px solid #000000',
                                        borderRight: '1px solid #000000',
                                        borderBottom: '1px solid #000000',
                                        borderLeft: firstColIdx === 0 ? '2px solid #000000' : '1px solid #000000',
                                        backgroundColor: '#e0e0e0',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        padding: '8px',
                                        position: 'sticky',
                                        top: topOffset,
                                        zIndex: 21,
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => {
                                        if (onHeaderGroupClick) {
                                          onHeaderGroupClick(group.id);
                                        }
                                      }}
                                    >
                                      {group.label}
                                    </th>
                                  );
                                } else {
                                  // This column is part of a group but not the first - skip it (covered by colspan)
                                  return null;
                                }
                              } else {
                                // This column is not part of any group at this level - this should not happen
                                // because columns not in any group should have rowSpan > 1 and be skipped above
                                return null;
                              }
                            })
                            .filter((element): element is React.ReactElement => element !== null)
                          }
                          {/* Add column button cell */}
                          <th
                            style={{
                              width: 60,
                              minWidth: 60,
                              borderTop: '2px solid #000000',
                              borderRight: '2px solid #000000',
                              borderBottom: '1px solid #000000',
                              borderLeft: '1px solid #000000',
                              backgroundColor: '#f5f5f5',
                              position: 'sticky',
                              top: topOffset,
                              zIndex: 21,
                            }}
                          />
                        </tr>
                      );
                    })}
                </>
              )}
              
              {/* Main column header row */}
              <tr style={{ height: 50 }}>
                {tableConfig.columns
                  .map((col, colIdx) => {
                    // NEW: Skip columns that are merged into another column (headerColSpan=0)
                    if ((col.headerColSpan || 1) === 0) return null;
                    
                    // NEW: Skip columns that are covered by a headerRow cell with rowspan
                    if (col.coveredByRowspan) return null;
                  
                  // Calculate how many rows this header should span
                  let rowSpan = calculateHeaderRowSpan(colIdx);
                  
                  // NEW: If column has skipHeaderRows, increase rowSpan to cover those rows
                  const numHeaderRows = tableConfig.headerRows?.length || 0;
                  if (col.skipHeaderRows && col.skipHeaderRows > 0) {
                    rowSpan += col.skipHeaderRows;
                  }
                  
                  // Calculate sticky top position - stack below headerRows and headerGroups
                  const numHeaderGroupLevels = tableConfig.headerGroups && tableConfig.headerGroups.length > 0
                    ? Math.max(...tableConfig.headerGroups.map(g => g.level)) + 1
                    : 0;
                  
                  // NEW: If column skips header rows, position it at the top (below add button)
                  const addButtonHeight = onAddHeaderRow ? 24 : 0;
                  const mainHeaderTop = col.skipHeaderRows && col.skipHeaderRows > 0
                    ? addButtonHeight // Start after add button
                    : addButtonHeight + (numHeaderRows * 40) + (numHeaderGroupLevels * 40); // Normal position
                  
                  // Check if this column is selected as header cell
                  // Use a special rowId for main columns: '__main_columns__'
                  const isMainColumnSelected = headerCellSelection.some(
                    sel => sel.rowId === '__main_columns__' && sel.cellId === col.id
                  );
                  
                  // Extract borders from column style
                  const colBorders = col.style?.borders || {};
                  const isFirstCol = colIdx === 0;
                  const isLastCol = colIdx === tableConfig.columns.length - 1;
                  const defaultBorderTop = 'none'; // FIXED: No top border on main columns (separator row handles the thick line)
                  const defaultBorderRight = isLastCol ? 'none' : '1px solid #d0d0d0'; // No right border on last col (handled by table border)
                  const defaultBorderBottom = 'none'; // Thick border is now in separator row
                  const defaultBorderLeft = isFirstCol ? 'none' : '1px solid #d0d0d0'; // No left border on first col (handled by table border)
                  
                  return (
                    <th
                      key={col.id}
                      rowSpan={rowSpan} // NEW: Use rowSpan for vertical merging!
                      colSpan={col.headerColSpan || 1} // Support horizontal merging
                      draggable={true}
                      // Header cell selection handlers
                      onClick={(e) => {
                        if (!e.defaultPrevented && onHeaderCellClick) {
                          e.stopPropagation();
                          onHeaderCellClick('__main_columns__', col.id, e.ctrlKey || e.metaKey);
                        }
                      }}
                      onDoubleClick={() => {
                        // Keep existing double-click behavior for rename dialog
                        if (onHeaderDoubleClick) {
                          onHeaderDoubleClick(colIdx);
                        }
                      }}
                      // Drag&Drop for column reorder
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('column-index', String(colIdx));
                        setDraggedColumnIndex(colIdx);
                        console.log('🔵 Column drag started:', colIdx, col.label);
                      }}
                      onDragEnd={() => {
                        setDraggedColumnIndex(null);
                        setDropIndicatorIndex(null);
                        console.log('🔵 Column drag ended');
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        if (draggedColumnIndex === null || draggedColumnIndex === colIdx) return;
                        
                        // Determine drop position based on mouse position
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        const midpoint = rect.left + rect.width / 2;
                        const dropIndex = e.clientX < midpoint ? colIdx : colIdx + 1;
                        
                        setDropIndicatorIndex(dropIndex);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        const fromIndex = draggedColumnIndex;
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        const midpoint = rect.left + rect.width / 2;
                        let toIndex = e.clientX < midpoint ? colIdx : colIdx + 1;
                        
                        console.log('🔵 Column dropped:', { fromIndex, toIndex, colIdx });
                        
                        if (fromIndex !== null && fromIndex !== toIndex) {
                          // Adjust toIndex if dragging from left to right
                          if (fromIndex < toIndex) {
                            toIndex--;
                          }
                          
                          if (onColumnReorder) {
                            console.log('✅ Calling onColumnReorder:', fromIndex, '->', toIndex);
                            onColumnReorder(fromIndex, toIndex);
                          }
                        }
                        
                        setDraggedColumnIndex(null);
                        setDropIndicatorIndex(null);
                      }}
                      style={{
                        position: 'relative', // CRITICAL: Needed for absolute positioned delete button!
                        width: col.width,
                        minWidth: col.width,
                        maxWidth: col.width,
                        // Apply custom borders if defined, otherwise use defaults
                        borderTop: colBorders.top
                          ? (colBorders.top.visible ? `${colBorders.top.width}px ${colBorders.top.style} ${colBorders.top.color}` : 'none')
                          : defaultBorderTop,
                        borderRight: colBorders.right
                          ? (colBorders.right.visible ? `${colBorders.right.width}px ${colBorders.right.style} ${colBorders.right.color}` : 'none')
                          : defaultBorderRight,
                        borderBottom: colBorders.bottom
                          ? (colBorders.bottom.visible ? `${colBorders.bottom.width}px ${colBorders.bottom.style} ${colBorders.bottom.color}` : 'none')
                          : defaultBorderBottom,
                        borderLeft: colBorders.left
                          ? (colBorders.left.visible ? `${colBorders.left.width}px ${colBorders.left.style} ${colBorders.left.color}` : 'none')
                          : defaultBorderLeft,
                        backgroundColor: col.style?.backgroundColor || (isMainColumnSelected
                          ? 'rgba(var(--md-primary-rgb, 103, 80, 164), 0.15)' // Same as headerRows
                          : draggedColumnIndex === colIdx 
                          ? 'rgba(var(--md-primary-rgb, 103, 80, 164), 0.3)'
                          : dropTargetColumn === colIdx
                          ? 'rgba(var(--md-primary-rgb, 103, 80, 164), 0.15)'
                          : col.label ? '#e8e8e8' : '#f8f8f8'), // Match headerRows
                        fontWeight: col.label ? 'bold' : 'normal', // Same as headerRows
                        textAlign: 'center',
                        padding: col.width < 30 ? '2px' : '8px',
                        overflow: 'visible',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: isMainColumnSelected ? 'pointer' : (draggedColumnIndex === colIdx ? 'grabbing' : 'grab'),
                        opacity: draggedColumnIndex === colIdx ? 0.5 : 1,
                      }}
                    >
                      {/* Drop indicator BEFORE column */}
                      {dropIndicatorIndex === colIdx && draggedColumnIndex !== null && (
                        <div
                          className="absolute top-0 bottom-0"
                          style={{
                            left: '-2px',
                            width: '4px',
                            backgroundColor: 'var(--md-primary)',
                            zIndex: 50,
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                      {/* Delete-Button - Oben rechts */}
                      {onDeleteColumn && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('🗑️ Column Header Trash clicked for column:', col.id, col.label);
                            console.log('🔍 attributeKey:', col.attributeKey);
                            const colName = col.label || col.attributeKey || '(Unbenannt)';
                            if (confirm(`Spalte \"${colName}\" wirklich löschen?\n\n⚠️ Achtung: Dies löscht nur die Spalte, nicht das Attribut aus den Bauteilen!`)) {
                              console.log('✅ User confirmed column delete');
                              onDeleteColumn(col.id);
                            } else {
                              console.log('❌ User cancelled column delete');
                            }
                          }}
                          className="absolute transition-opacity hover:opacity-100"
                          style={{
                            top: '4px',
                            right: '4px',
                            color: '#d32f2f',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '4px',
                            padding: '4px',
                            cursor: 'pointer',
                            border: '1px solid rgba(211, 47, 47, 0.5)',
                            zIndex: 40,
                            opacity: 0.6, // Deutlich sichtbarer als vorher (war 0.3)
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          }}
                          title="Spalte löschen"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                      
                      {/* Column Label - Shows merged icon and text */}
                      <div 
                        className="overflow-hidden text-ellipsis whitespace-nowrap flex items-center justify-center gap-1"
                        style={{
                          padding: '4px',
                          whiteSpace: 'pre-wrap', // Support line breaks
                          wordBreak: 'break-word',
                        }}
                      >
                        {/* Merged icon - show for merged columns */}
                        {col.isMerged && (
                          <Link2 
                            className="w-4 h-4 flex-shrink-0" 
                            style={{ color: 'var(--md-primary)' }}
                            title={`Zusammengeführt aus: ${col.mergedFrom?.join(', ')}`}
                          />
                        )}
                        <span style={{ color: col.label ? 'inherit' : '#999', fontStyle: col.label ? 'normal' : 'italic' }}>
                          {col.label || col.attributeKey || '(Unbenannt)'}
                        </span>
                      </div>
                      {/* Resize Handle - Larger hit area for better UX */}
                      <div
                        className="absolute top-0 bottom-0 cursor-ew-resize flex items-center justify-center"
                        style={{
                          right: '-6px',
                          width: '12px',
                          backgroundColor: 'transparent',
                          zIndex: 30,
                        }}
                        onMouseEnter={() => setHoveredResizeColumn(col.id)}
                        onMouseLeave={() => !resizeStateRef.current && setHoveredResizeColumn(null)}
                        onMouseDown={(e) => handleResizeStart(e, col.id, col.width)}
                        onDoubleClick={(e) => handleAutoFit(e, colIdx)}
                        title="Ziehen für Breite | Doppelklick für Auto-Fit"
                      >
                        {/* Visual indicator - NO transition for instant response */}
                        <div
                          className="h-full"
                          style={{
                            width: hoveredResizeColumn === col.id || resizeStateRef.current?.columnId === col.id ? '3px' : '1px',
                            backgroundColor: resizeStateRef.current?.columnId === col.id 
                              ? 'var(--md-primary)' 
                              : hoveredResizeColumn === col.id
                              ? 'rgba(var(--md-primary-rgb, 103, 80, 164), 0.5)'
                              : 'transparent',
                            boxShadow: (hoveredResizeColumn === col.id || resizeStateRef.current?.columnId === col.id)
                              ? '0 0 4px rgba(var(--md-primary-rgb, 103, 80, 164), 0.4)'
                              : 'none',
                          }}
                        />
                      </div>
                    </th>
                  );
                })
                .filter((element): element is React.ReactElement => element !== null)
              }
              </tr>
              
              {/* Separator row with thick border between header and data */}
              <tr style={{ 
                height: 0,
                borderTop: '3px solid #000000', // Border direkt auf TR, geht über gesamte Breite
              }}>
                <td
                  colSpan={tableConfig.columns.length}
                  style={{
                    padding: 0,
                    height: 0,
                    borderTop: 'none', // Border ist jetzt auf TR
                    borderBottom: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                  }}
                />
              </tr>
            </thead>
            <tbody>
              {tableConfig.rows
                .filter(row => !row.isHeader)
                .filter(row => {
                  // 🔥 SKIP EMPTY ROWS: Filter out rows where ALL cells are empty
                  const cells = Object.values(row.data);
                  if (cells.length === 0) return false;
                  
                  // Check if at least ONE cell has meaningful content
                  const hasContent = cells.some((cell: any) => {
                    const val = cell.value;
                    return val !== '' && val !== null && val !== undefined;
                  });
                  
                  return hasContent;
                })
                .map((row, rowIdx, dataRows) => {
                  // Define isLastRow once for the entire row
                  const isLastRow = rowIdx === dataRows.length - 1;
                  
                  // ===== GROUP HEADER ROW =====
                  if (row.isGroupHeader) {
                    return (
                      <tr
                        key={row.id}
                        style={{
                          height: row.height,
                          backgroundColor: '#e8e8e8',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          if (onToggleGroupCollapse && row.groupKey) {
                            onToggleGroupCollapse(row.groupKey);
                          }
                        }}
                      >
                        {tableConfig.columns.map((col, colIdx) => {
                          const cell = row.data[col.id];
                          const isGroupColumn = col.id === row.groupColumnId;
                          const indentation = (row.groupLevel || 0) * 20;
                          
                          if (colIdx === 0 || isGroupColumn) {
                            return (
                              <td
                                key={cell?.id || `${row.id}-${col.id}`}
                                style={{
                                  paddingLeft: colIdx === 0 ? `${8 + indentation}px` : '8px',
                                  borderTop: '1px solid #d0d0d0',
                                  borderBottom: '1px solid #d0d0d0',
                                  borderLeft: colIdx === 0 ? 'none' : '1px solid #d0d0d0',
                                  borderRight: colIdx === tableConfig.columns.length - 1 ? 'none' : '1px solid #d0d0d0',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  {colIdx === 0 && (
                                    row.collapsed ? 
                                      <ChevronRight className="w-4 h-4" /> : 
                                      <ChevronDown className="w-4 h-4" />
                                  )}
                                  {isGroupColumn && (
                                    <span>{cell?.value || ''} ({row.childCount || 0})</span>
                                  )}
                                </div>
                              </td>
                            );
                          }
                          
                          return (
                            <td
                              key={cell?.id || `${row.id}-${col.id}`}
                              style={{
                                borderTop: '1px solid #d0d0d0',
                                borderBottom: '1px solid #d0d0d0',
                                borderLeft: '1px solid #d0d0d0',
                                borderRight: colIdx === tableConfig.columns.length - 1 ? 'none' : '1px solid #d0d0d0',
                              }}
                            />
                          );
                        })}
                      </tr>
                    );
                  }
                  
                  // ===== SUBTOTAL ROW =====
                  if (row.isSubtotal) {
                    return (
                      <tr
                        key={row.id}
                        style={{
                          height: row.height,
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          fontStyle: 'italic',
                        }}
                      >
                        {tableConfig.columns.map((col, colIdx) => {
                          const cell = row.data[col.id];
                          const indentation = (row.groupLevel || 0) * 20;
                          
                          return (
                            <td
                              key={cell?.id || `${row.id}-${col.id}`}
                              style={{
                                paddingLeft: colIdx === 0 ? `${8 + indentation}px` : '8px',
                                paddingRight: '8px',
                                textAlign: col.dataType === 'number' ? 'right' : 'left',
                                borderTop: '2px solid #888',
                                borderBottom: '1px solid #d0d0d0',
                                borderLeft: colIdx === 0 ? 'none' : '1px solid #d0d0d0',
                                borderRight: colIdx === tableConfig.columns.length - 1 ? 'none' : '1px solid #d0d0d0',
                              }}
                            >
                              {colIdx === 0 ? 'Zwischensumme' : (cell?.value || '')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  }
                  
                  // ===== GRAND TOTAL ROW =====
                  if (row.isGrandTotal) {
                    return (
                      <tr
                        key={row.id}
                        style={{
                          height: row.height,
                          backgroundColor: '#d0d0d0',
                          fontWeight: 'bold',
                        }}
                      >
                        {tableConfig.columns.map((col, colIdx) => {
                          const cell = row.data[col.id];
                          
                          return (
                            <td
                              key={cell?.id || `${row.id}-${col.id}`}
                              style={{
                                paddingLeft: '8px',
                                paddingRight: '8px',
                                textAlign: col.dataType === 'number' ? 'right' : 'left',
                                borderTop: '3px solid #000',
                                borderBottom: 'none',
                                borderLeft: colIdx === 0 ? 'none' : '1px solid #888',
                                borderRight: colIdx === tableConfig.columns.length - 1 ? 'none' : '1px solid #888',
                              }}
                            >
                              {colIdx === 0 ? 'Gesamtsumme' : (cell?.value || '')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  }
                  
                  // ===== NORMAL DATA ROW =====
                  return (
                  <tr
                    key={row.id}
                    style={{ 
                      height: row.height,
                      position: 'relative',
                      zIndex: 1 // Lower than header z-index (20-30)
                    }}
                    onMouseEnter={() => setHoveredRow(row.index)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    {tableConfig.columns.map((col, colIdx) => {
                      const cell = row.data[col.id];
                      
                      // If cell doesn't exist, render empty td
                      if (!cell) {
                        return <td key={`${row.id}-${col.id}-empty`} />;
                      }

                      const renderInfo = getCellRenderInfo(cell, tableConfig);
                      
                      // If cell is merged (part of colspan/rowspan), don't render it
                      if (!renderInfo.shouldRender) {
                        return null;
                      }

                      const isSelected = selection &&
                        row.index >= selection.startRow &&
                        row.index <= selection.endRow &&
                        colIdx >= selection.startCol &&
                        colIdx <= selection.endCol;

                      const isFirstColumn = colIdx === 0;
                      const isLastColumn = colIdx === tableConfig.columns.length - 1;

                      return (
                        <TableCell
                          key={cell.id}
                          cell={cell}
                          width={renderInfo.width}
                          height={renderInfo.height}
                          isSelected={isSelected}
                          isEditing={editingCell?.rowId === row.id && editingCell?.colId === col.id}
                          showGridLines={tableConfig.showGridLines}
                          isLocked={false}
                          isFirstColumn={isFirstColumn}
                          isLastColumn={isLastColumn}
                          isLastRow={isLastRow}
                          onMouseDown={(e) => handleCellMouseDown(row.index, colIdx, e)}
                          onMouseEnter={() => handleCellMouseEnter(row.index, colIdx)}
                          onDoubleClick={() => handleCellDoubleClick(row.id, col.id)}
                          onValueChange={(value) => onCellValueChange(row.id, col.id, value)}
                          onEditEnd={() => onCellEdit(null)}
                        />
                      );
                    })}
                  </tr>
                  );
                })}
                
                {/* Totals Row */}
                {tableConfig.totalsRowConfig?.enabled && onUpdateTotalsAggregation && (
                  <TotalsRow
                    columns={tableConfig.columns}
                    totalsRowConfig={tableConfig.totalsRowConfig}
                    totalsRowData={totalsRowData}
                    onUpdateAggregation={onUpdateTotalsAggregation}
                  />
                )}
            </tbody>
          </table>
          
          {/* Add Column Area - always visible next to table */}
          <div
            className="flex items-center justify-center cursor-pointer transition-colors"
            onDragOver={(e) => handleDragOver(e, tableConfig.columns.length)}
            onDrop={(e) => handleDrop(e, tableConfig.columns.length)}
            onDragLeave={handleDragLeave}
            onClick={onAddColumn}
            style={{
              width: '48px',
              minWidth: '48px',
              flexShrink: 0,
              backgroundColor: dropTargetColumn === tableConfig.columns.length 
                ? 'rgba(var(--md-primary-rgb, 103, 80, 164), 0.1)' 
                : '#f5f5f5',
              borderLeft: '2px solid #000000',
              borderTop: '3px solid #000000',
              borderRight: '3px solid #000000',
              borderBottom: '3px solid #000000',
            }}
            title="Spalte hinzufügen"
          >
            <Plus 
              className="w-6 h-6" 
              style={{ color: 'var(--md-primary)' }}
            />
          </div>
        </div>
      )}
      {/* Resize preview line */}
      {resizePreviewX !== null && (
        <div
          className="absolute top-0 bottom-0"
          style={{
            left: `${resizePreviewX}px`,
            width: '1px',
            backgroundColor: 'var(--md-primary)',
            zIndex: 10,
          }}
        />
      )}
      
      {/* Header Cell features removed */}
    </div>
  );
}

// Individual cell component
interface TableCellProps {
  cell: TableCellType;
  width: number;
  height: number;
  isSelected: boolean;
  isEditing: boolean;
  showGridLines: boolean;
  isLocked: boolean;
  isFirstColumn?: boolean;
  isLastColumn?: boolean;
  isLastRow?: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onDoubleClick: () => void;
  onValueChange: (value: string | number | boolean) => void;
  onEditEnd: () => void;
}

function TableCell({
  cell,
  width,
  height,
  isSelected,
  isEditing,
  showGridLines,
  isLocked,
  isFirstColumn = false,
  isLastColumn = false,
  isLastRow = false,
  onMouseDown,
  onMouseEnter,
  onDoubleClick,
  onValueChange,
  onEditEnd,
}: TableCellProps) {
  const [editValue, setEditValue] = useState(String(cell.value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(String(cell.value));
  }, [cell.value]);

  const handleBlur = () => {
    let finalValue: string | number | boolean = editValue;
    
    if (cell.dataType === 'number') {
      const parsed = parseFloat(editValue);
      finalValue = isNaN(parsed) ? 0 : parsed;
    } else if (cell.dataType === 'boolean') {
      finalValue = editValue.toLowerCase() === 'true' || editValue === '1';
    }

    onValueChange(finalValue);
    onEditEnd();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setEditValue(String(cell.value));
      onEditEnd();
    }
  };

  const style = cell.style || {};
  const borders = style.borders || {};

  // 3-Level Border Hierarchy:
  // LEVEL 1: Table outer border (3px black) - handled by <table> border
  // LEVEL 2: Area boundaries (2-3px black) - first column right border
  // LEVEL 3: Inner grid (1px light gray) - all other borders
  
  const borderStyle: React.CSSProperties = showGridLines ? {
    // Top border: LEVEL 3 (inner grid)
    borderTop: borders.top 
      ? (borders.top.visible ? `${borders.top.width}px ${borders.top.style} ${borders.top.color}` : 'none')
      : '1px solid #d0d0d0',
    
    // Right border: LEVEL 2 after first column, LEVEL 3 for others, none for last column (table border handles it)
    borderRight: borders.right
      ? (borders.right.visible ? `${borders.right.width}px ${borders.right.style} ${borders.right.color}` : 'none')
      : isLastColumn 
        ? 'none' 
        : isFirstColumn 
          ? '2px solid #000000' 
          : '1px solid #d0d0d0',
    
    // Bottom border: LEVEL 3 or none for last row (table border handles it)
    borderBottom: borders.bottom
      ? (borders.bottom.visible ? `${borders.bottom.width}px ${borders.bottom.style} ${borders.bottom.color}` : 'none')
      : isLastRow 
        ? 'none' 
        : '1px solid #d0d0d0',
    
    // Left border: none for first column (table border handles it), LEVEL 3 for others
    borderLeft: borders.left
      ? (borders.left.visible ? `${borders.left.width}px ${borders.left.style} ${borders.left.color}` : 'none')
      : isFirstColumn 
        ? 'none' 
        : '1px solid #d0d0d0',
  } : {};

  return (
    <td
      style={{
        width,
        height,
        minWidth: width,
        maxWidth: width,
        minHeight: height,
        maxHeight: height,
        backgroundColor: isSelected 
          ? 'rgba(var(--md-primary-rgb, 103, 80, 164), 0.15)' 
          : style.backgroundColor || '#ffffff',
        color: style.textColor || '#000000',
        fontWeight: style.fontWeight || 'normal',
        fontSize: style.fontSize || 12,
        textAlign: style.textAlign || 'left',
        verticalAlign: style.verticalAlign || 'middle',
        padding: width < 30 ? '2px' : (style.padding || 8), // Reduce padding for narrow columns
        cursor: cell.isReadOnly ? 'default' : 'cell',
        userSelect: 'none',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        position: 'relative',
        zIndex: 1, // Lower than header cells (20-30)
        ...borderStyle,
      }}
      rowSpan={cell.merge?.rowSpan}
      colSpan={cell.merge?.colSpan}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onDoubleClick={onDoubleClick}
    >
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-full h-full bg-transparent outline-none border-2"
          style={{
            borderColor: 'var(--md-primary)',
            padding: '2px 4px',
          }}
        />
      ) : (
        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
          {(() => {
            const displayValue = String(cell.value ?? '');
            // Debug log für leere Zellen
            if (displayValue === '' || displayValue === 'null' || displayValue === 'undefined') {
              console.log('🔴 Leere Zelle:', { cellId: cell.id, value: cell.value, dataType: cell.dataType });
            }
            return displayValue;
          })()}
        </div>
      )}
    </td>
  );
}